/**
 * LEGACY_EDITOR — la barre flottante de l'ancien éditeur (master : lib/fabric_controls), sur
 * la lib actuelle. Elle branche la sélection de l'éditeur sur les boutons que la vue a rendus
 * (documents/editors/legacy/_layer_controls, par `data-role`), les montre selon l'objet et
 * les pose au-dessus de lui ; elle relaie à l'hôte (onModified) après chaque mutation.
 *
 * Ce qui n'existe plus dans la lib est réécrit ici par-dessus son API : « repositionner
 * l'image dans son cadre » (le drag d'`offsetX/offsetY` de master) ouvre le recadrage de la
 * lib (`editor.crop`), qui gère le geste et se ferme seul ; l'hôte n'enregistre qu'à sa fin.
 *
 * Usage :
 *   const controls = new LegacyFabricControls(editor, controlsEl, {
 *     onModified: () => { ... },
 *     ...editor.getControlsConfig(anchorEl),
 *   });
 *   controls.attach();
 *   controls.destroy();
 */
import {
  FabricEditor,
  ImageFrame,
  getLockMode,
  isPositionLocked,
  isStyleLocked,
  rulesOf,
} from "@frabr/fabric-editor";
import type { FabricObject } from "fabric";
import { mediumMeta } from "../composition_document";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FabricObj = FabricObject & Record<string, any>

interface LegacyFabricControlsOpts {
  onModified?: () => void
  getContainer: () => HTMLElement
  getDisplayScale?: () => number
  getCanvasOffset?: () => { left: number; top: number }
}

/** Un média de la bibliothèque : sa source, et son id — qui voyage avec elle dans le calque. */
interface ContentMediumRef {
  id?: number | string | null
  url?: string | null
}

const HEX_COLOR = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
/** L'écart entre la barre et la sélection, et entre le bouton rond et l'image. */
const GAP = 14;
const BUTTON_GAP = 8;

export class LegacyFabricControls {
  editor: FabricEditor
  controlsEl: HTMLElement
  onModified: () => void
  getContainer: () => HTMLElement
  getDisplayScale: () => number
  getCanvasOffset: () => { left: number; top: number }

  private readonly onCropEnd = (e: { changed?: boolean }) => {
    if (e.changed) this.onModified();
  };

  constructor(editor: FabricEditor, controlsEl: HTMLElement, opts: LegacyFabricControlsOpts) {
    this.editor = editor;
    this.controlsEl = controlsEl;
    this.onModified = opts.onModified || (() => {});
    this.getContainer = opts.getContainer;
    this.getDisplayScale = opts.getDisplayScale || (() => 1);
    this.getCanvasOffset = opts.getCanvasOffset || (() => ({ left: 0, top: 0 }));
  }

  attach(): void {
    this.editor.selection.onSelect = (obj: FabricObj) => this._showControls(obj);
    this.editor.selection.onDeselect = () => this.hide();
    this.editor.selection.onTransformStart = () => this.hide();
    this.editor.selection.onModified = (obj: FabricObj | null) => {
      this.onModified();
      if (obj) this._showControls(obj);
    };
    this.editor.canvas.on("crop:end", this.onCropEnd);

    const current = this.editor.selection.current as FabricObj | null;
    if (current) this._showControls(current);
  }

  destroy(): void {
    this.editor.selection.onSelect = undefined;
    this.editor.selection.onDeselect = undefined;
    this.editor.selection.onTransformStart = undefined;
    this.editor.selection.onModified = undefined;
    this.editor.canvas.off("crop:end", this.onCropEnd);
  }

  hide(): void {
    this.controlsEl.classList.add("hidden");
    this._updateRepositionButton(null);
  }

  /** Après un redimensionnement du conteneur : la barre suit la sélection. */
  repositionActive(): void {
    const obj = this.editor.selection.current as FabricObj | null;
    if (obj && !this.controlsEl.classList.contains("hidden")) {
      this._positionControls(obj);
      this._updateRepositionButton(obj);
    }
  }

  // ==================== Actions ====================

  deleteSelection(): void {
    this.editor.deleteSelection();
    this.hide();
    this.onModified();
  }

  /** La forme change en cycle (lib `@legacy`) : la découpe d'une image, la forme d'un calque. */
  switchShape(): void {
    const obj = this.restylable();
    if (!obj) return;

    if (rulesOf(obj).options.includes("image")) this.editor.switchClip();
    else this.editor.switchShape();
    this.editor.canvas.renderAll();
    this.onModified();
  }

  changeColor(color: string): void {
    if (!this.restylable() || !HEX_COLOR.test(color || "")) return;
    this.editor.changeColor(color);
    this._updateColorDisplay();
    this.onModified();
  }

  /** @param opacity en pourcentage (0-100), comme l'input */
  changeOpacity(opacity: number): void {
    if (!this.restylable()) return;
    this.editor.changeOpacity(opacity);
    this.onModified();
  }

  changeFont(fontFamily: string, fontWeight?: string): void {
    if (!this.restylable() || !fontFamily) return;
    this.editor.changeFont(fontFamily, fontWeight);
    this.onModified();
  }

  bringForward(): void {
    const obj = this.restackable();
    if (!obj) return;
    this.editor.layers.bringForward(obj);
    this.onModified();
  }

  sendBackward(): void {
    const obj = this.restackable();
    if (!obj) return;
    this.editor.layers.sendBackward(obj);
    this.onModified();
  }

  addShape(opts: Record<string, unknown> = {}) {
    return this.editor.layers.addShape(opts);
  }

  addText(opts: Record<string, unknown> = {}) {
    return this.editor.layers.addText({
      text: "Texte",
      fontFamily: "Inter",
      fontSize: 32,
      fill: "#000000",
      ...opts,
    });
  }

  async addImage(cm: ContentMediumRef): Promise<ImageFrame | null> {
    if (!cm.url) return null;
    return this.editor.layers.addImage(cm.url, { imageMeta: mediumMeta(cm) });
  }

  /** L'image du cadre sélectionné — y compris verrouillé en position (le « placeholder »). */
  async replaceSelectedImage(cm: ContentMediumRef): Promise<void> {
    const obj = this.editor.selection.current as FabricObj | null;
    if (!cm.url || !obj || getLockMode(obj) === "full") return;
    if (!rulesOf(obj).options.includes("image")) return;

    await this.editor.layers.replaceImageSource(obj, cm.url, { imageMeta: mediumMeta(cm) });
    this._updateRepositionButton(obj);
    this.onModified();
  }

  /** Repositionner l'image dans son cadre : le recadrage de la lib, sur le cadre sélectionné. */
  startReposition(): void {
    const obj = this.editor.selection.current;
    if (obj instanceof ImageFrame && obj.croppable && getLockMode(obj) !== "full") this.editor.crop.start(obj);
  }

  /** Le sélecteur natif, depuis la pastille du menu en place. */
  openColorPicker(): void {
    const obj = this.editor.selection.current as FabricObj | null;
    const input = this._el<HTMLInputElement>("colorInput");
    if (!obj?.fill || !input) return;
    input.value = obj.fill as string;
    input.click();
  }

  syncOpacityInput(): void {
    const obj = this.editor.selection.current as FabricObj | null;
    const input = this._el<HTMLInputElement>("opacityInput");
    if (obj && input) input.value = String(Math.round((obj.opacity ?? 1) * 100));
  }

  // ==================== Rendu ====================

  private restylable(): FabricObj | null {
    const obj = this.editor.selection.current as FabricObj | null;
    return obj && !isStyleLocked(obj) && rulesOf(obj).restyles ? obj : null;
  }

  private restackable(): FabricObj | null {
    const obj = this.editor.selection.current as FabricObj | null;
    return obj && getLockMode(obj) === "free" && rulesOf(obj).restacks ? obj : null;
  }

  private _showControls(obj: FabricObj): void {
    this._updateColorDisplay();
    this.syncOpacityInput();
    this._activateControls(obj);
    this.controlsEl.classList.remove("hidden");
    this._positionControls(obj);
    this._updateDeleteButton(obj);
    this._updateRepositionButton(obj);
  }

  /** Au-dessus de la sélection, centrée, en dessous faute de place, dans le conteneur. */
  private _positionControls(obj: FabricObj): void {
    const container = this.getContainer();
    if (!container) return;

    const rect = this._displayRect(obj);
    const controlsW = this.controlsEl.offsetWidth || 200;
    const controlsH = this.controlsEl.offsetHeight || 36;

    let left = rect.left + rect.width / 2 - controlsW / 2;
    let top = rect.top - controlsH - GAP;
    if (top < 0) top = rect.top + rect.height + GAP;

    left = Math.max(0, Math.min(left, container.clientWidth - controlsW));
    top = Math.max(0, Math.min(top, container.clientHeight - controlsH));

    Object.assign(this.controlsEl.style, { position: "absolute", left: `${left}px`, top: `${top}px`, transform: "none" });
  }

  private _displayRect(obj: FabricObj): { left: number; top: number; width: number; height: number } {
    const scale = this.getDisplayScale();
    const bound = obj.getBoundingRect();
    const offset = this.getCanvasOffset();
    return {
      left: bound.left * scale + offset.left,
      top: bound.top * scale + offset.top,
      width: bound.width * scale,
      height: bound.height * scale,
    };
  }

  private _activateControls(obj: FabricObj): void {
    const roles = ["clipButton", "replaceImageButton", "colorPicker", "fontButton", "upButton", "downButton"];
    roles.forEach((role) => this._el(role)?.classList.add("hidden"));

    const lockMode = getLockMode(obj);
    if (lockMode === "full") return;

    const takesImage = rulesOf(obj).options.includes("image");
    // Le « placeholder » du legacy : verrouillé en position, seule son image se remplace.
    if (lockMode === "position") {
      if (takesImage) this._el("replaceImageButton")?.classList.remove("hidden");
      return;
    }

    this.editor.selection.getAvailableControls().forEach((control) => {
      switch (control) {
        case "clip": this._el("clipButton")?.classList.remove("hidden"); break;
        case "color": this._el("colorPicker")?.classList.remove("hidden"); break;
        case "font": this._el("fontButton")?.classList.remove("hidden"); break;
      }
    });
    if (takesImage) this._el("replaceImageButton")?.classList.remove("hidden");
    if (rulesOf(obj).restacks) {
      this._el("upButton")?.classList.remove("hidden");
      this._el("downButton")?.classList.remove("hidden");
    }
  }

  private _updateDeleteButton(obj: FabricObj | null): void {
    const el = this._el("deleteButton");
    if (!el) return;
    el.classList.toggle("hidden", !obj || isPositionLocked(obj) || !rulesOf(obj).deletes);
  }

  /** Le menu en place montre la couleur de l'objet. */
  private _updateColorDisplay(): void {
    const fill = (this.editor.selection.current as FabricObj | null)?.fill;
    if (typeof fill !== "string") return;
    const current = this._el<HTMLElement>("currentColor");
    if (current) current.style.backgroundColor = fill;
    // Le contenu du menu n'est dans le DOM qu'une fois ouvert (floating-menu) : on repasse après.
    setTimeout(() => {
      const hexInput = this._el<HTMLInputElement>("hexColorInput");
      const trigger = this._el<HTMLElement>("colorInputTrigger");
      if (hexInput) hexInput.value = fill;
      if (trigger) trigger.style.backgroundColor = fill;
    }, 0);
  }

  /** Le bouton rond, à gauche de l'image (à droite faute de place) — pour un vrai cadre image. */
  private _updateRepositionButton(obj: FabricObj | null): void {
    const btn = this._el<HTMLElement>("repositionButton");
    if (!btn) return;

    const shown = obj instanceof ImageFrame && obj.croppable && getLockMode(obj) !== "full";
    btn.classList.toggle("hidden", !shown);
    if (!shown || !obj) return;

    const rect = this._displayRect(obj);
    let left = rect.left - btn.offsetWidth - BUTTON_GAP;
    if (left < 0) left = rect.left + rect.width + BUTTON_GAP;
    const top = rect.top + rect.height / 2 - btn.offsetHeight / 2;
    Object.assign(btn.style, { position: "absolute", left: `${left}px`, top: `${top}px` });
  }

  /** Un élément de la barre — ou des menus en place, ouverts hors de la barre dans l'ancre. */
  private _el<T extends Element = Element>(role: string): T | null {
    const selector = `[data-role='${role}']`;
    return this.controlsEl.querySelector<T>(selector) || this.getContainer()?.querySelector<T>(selector) || null;
  }
}
